import { useCallback, useEffect, useRef, useState } from 'react';

import { getSweepViewportRange } from '../math/layout';

import type { RefObject } from 'react';

const ALLOCATION_LAYOUT_SETTLE_DELAY_MS = 120;

function areAllocationViewportsEqual(
    left: { endMHz: number; startMHz: number } | null,
    right: { endMHz: number; startMHz: number } | null,
): boolean {
    if (left === right) {
        return true;
    }

    if (!left || !right) {
        return false;
    }

    return Math.abs(left.startMHz - right.startMHz) < 0.001 && Math.abs(left.endMHz - right.endMHz) < 0.001;
}

export interface UseAllocationViewportTrackingParams {
    readonly allocationOverlayEnabled: boolean;
    readonly containerRef: RefObject<HTMLDivElement | null>;
    readonly leftPadPx: number;
    readonly pxPerMHz: number;
    readonly totalMHz: number;
}

export default function useAllocationViewportTracking({
    allocationOverlayEnabled,
    containerRef,
    leftPadPx,
    pxPerMHz,
    totalMHz,
}: Readonly<UseAllocationViewportTrackingParams>) {
    const [allocationViewport, setAllocationViewport] = useState<{ endMHz: number; startMHz: number } | null>(null);
    const [allocationLayoutPxPerMHz, setAllocationLayoutPxPerMHz] = useState<number>(pxPerMHz);

    const allocationViewportRafIdRef = useRef<number | null>(null);
    const lastAllocationViewportRef = useRef<{ endMHz: number; startMHz: number } | null>(null);
    const allocationCommitTimeoutRef = useRef<number | null>(null);
    const pendingAllocationViewportRef = useRef<{ endMHz: number; startMHz: number } | null>(null);
    const pendingAllocationPxPerMHzRef = useRef<number>(pxPerMHz);
    const allocationLayoutInitializedRef = useRef<boolean>(false);

    const commitPendingAllocationLayout = useCallback(() => {
        allocationCommitTimeoutRef.current = null;
        allocationLayoutInitializedRef.current = true;

        const nextPxPerMHz = pendingAllocationPxPerMHzRef.current;
        const nextViewport = pendingAllocationViewportRef.current;

        setAllocationLayoutPxPerMHz((currentValue) => {
            if (Math.abs(currentValue - nextPxPerMHz) < 0.0001) {
                return currentValue;
            }

            return nextPxPerMHz;
        });
        setAllocationViewport((currentValue) => {
            if (areAllocationViewportsEqual(currentValue, nextViewport)) {
                return currentValue;
            }

            return nextViewport;
        });
    }, []);

    const scheduleAllocationLayoutCommit = useCallback(
        (immediate = false) => {
            if (allocationCommitTimeoutRef.current !== null) {
                window.clearTimeout(allocationCommitTimeoutRef.current);
                allocationCommitTimeoutRef.current = null;
            }

            if (immediate || !allocationLayoutInitializedRef.current) {
                commitPendingAllocationLayout();
                return;
            }

            allocationCommitTimeoutRef.current = window.setTimeout(() => {
                commitPendingAllocationLayout();
            }, ALLOCATION_LAYOUT_SETTLE_DELAY_MS);
        },
        [commitPendingAllocationLayout],
    );

    useEffect(() => {
        return () => {
            if (allocationCommitTimeoutRef.current !== null) {
                window.clearTimeout(allocationCommitTimeoutRef.current);
                allocationCommitTimeoutRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        pendingAllocationPxPerMHzRef.current = pxPerMHz;

        if (!allocationOverlayEnabled) {
            pendingAllocationViewportRef.current = null;
            allocationLayoutInitializedRef.current = false;

            if (allocationCommitTimeoutRef.current !== null) {
                window.clearTimeout(allocationCommitTimeoutRef.current);
                allocationCommitTimeoutRef.current = null;
            }

            setAllocationLayoutPxPerMHz(pxPerMHz);
            setAllocationViewport(null);
            return;
        }

        scheduleAllocationLayoutCommit();
    }, [allocationOverlayEnabled, pxPerMHz, scheduleAllocationLayoutCommit]);

    useEffect(() => {
        const container = containerRef.current;

        if (!container || !allocationOverlayEnabled) {
            pendingAllocationViewportRef.current = null;
            return;
        }

        const currentContainer = container;

        function publishAllocationViewport() {
            allocationViewportRafIdRef.current = null;

            const nextViewport = getSweepViewportRange({
                leftPadPx,
                pxPerMHz,
                scrollLeft: currentContainer.scrollLeft,
                totalMHz,
                viewportWidth: currentContainer.clientWidth,
            });
            const previousViewport = lastAllocationViewportRef.current;

            if (
                previousViewport &&
                Math.abs(previousViewport.startMHz - nextViewport.startMHz) < 0.001 &&
                Math.abs(previousViewport.endMHz - nextViewport.endMHz) < 0.001
            ) {
                return;
            }

            const compactViewport = {
                endMHz: nextViewport.endMHz,
                startMHz: nextViewport.startMHz,
            };

            lastAllocationViewportRef.current = compactViewport;
            pendingAllocationViewportRef.current = compactViewport;
            scheduleAllocationLayoutCommit();
        }

        function scheduleAllocationViewportPublish() {
            if (allocationViewportRafIdRef.current !== null) {
                return;
            }

            allocationViewportRafIdRef.current = requestAnimationFrame(() => {
                publishAllocationViewport();
            });
        }

        let observer: ResizeObserver | null = null;
        if (typeof ResizeObserver !== 'undefined') {
            observer = new ResizeObserver(() => {
                scheduleAllocationViewportPublish();
            });
            observer.observe(container);
        }

        scheduleAllocationViewportPublish();
        currentContainer.addEventListener('scroll', scheduleAllocationViewportPublish, { passive: true });

        return () => {
            currentContainer.removeEventListener('scroll', scheduleAllocationViewportPublish);
            if (observer) {
                observer.disconnect();
            }
            if (allocationViewportRafIdRef.current !== null) {
                cancelAnimationFrame(allocationViewportRafIdRef.current);
                allocationViewportRafIdRef.current = null;
            }
        };
    }, [allocationOverlayEnabled, containerRef, leftPadPx, pxPerMHz, scheduleAllocationLayoutCommit, totalMHz]);

    return { allocationLayoutPxPerMHz, allocationViewport };
}
